import { usePeriod } from '@/app/context/PeriodContext';
import { SharingResult, SharingResultDebit } from '@/app/models/sharingResult';
import { useGetActiveBuildingsQuery } from '@/app/services/buildingsApi';
import { useGetSharingResultByPeriodAndBuildingQuery } from '@/app/services/sharedAccountingExpenseApi';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import SharingResultsTable from '@/components/sharingDetail/SharingResultsTable';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Data, getComparator, Order } from '../buildingsTotal';

const formatter = new Intl.NumberFormat('pt-BR');

const RowTableRow = styled(TableCell)(() => ({
  fontSize: 13,
  backgroundColor: 'white',
  borderBottom: 'none',
}));

const RowTableCell = styled(TableCell)(() => ({
  fontSize: 13,
  backgroundColor: '#fafafa',
  borderBottom: 'none',
}));

function ExpandableSharingDetailRow(props: { row: SharingResult }) {
  const { t } = useTranslation(['sharingDetail', 'common']);
  const { row } = props;
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          backgroundColor: '#f9f9f9',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(!open)}
      >
        <RowTableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>

          {`${row.projectCode} - ${row.projectName}`}
        </RowTableCell>
        <RowTableCell>{`R$ ${formatter.format(row.value)}`}</RowTableCell>
        <RowTableCell sx={{ color: 'blue' }}>{t(row.operation)}</RowTableCell>
        <RowTableCell>{`${row.expenseCode} - ${row.expenseName}`}</RowTableCell>
        <RowTableCell>
          <Tooltip
            arrow
            enterDelay={200}
            placement="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#F7F7F7',
                  minWidth: '30rem',
                  border: '1px solid #D9D9D9',
                  color: 'black',
                  '& .MuiTooltip-arrow': {
                    color: '#F7F7F7',
                    '&:before': {
                      border: '1px solid #D9D9D9',
                    },
                  },
                },
              },
            }}
            title={row.supplier}
          >
            <span>{`${row.supplier.slice(0, 70)}...`}</span>
          </Tooltip>
        </RowTableCell>
      </TableRow>

      {open && (
        <TableRow>
          <TableCell style={{ padding: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box>
                <Table size="small" aria-label="sub-items" padding="none">
                  <TableBody>
                    {JSON.parse(row.debits)?.map((subRow: SharingResultDebit) => (
                      <TableRow key={subRow.projectCode} sx={{ backgroundColor: 'white' }}>
                        <RowTableRow
                          component="th"
                          scope="row"
                          sx={{
                            paddingLeft: 5,
                            paddingRight: (theme) => theme.spacing(2),
                            paddingTop: (theme) => theme.spacing(1),
                            paddingBottom: (theme) => theme.spacing(1),
                            width: '20.5%',
                          }}
                        >
                          {subRow.projectCode}
                        </RowTableRow>
                        <RowTableRow
                          align="left"
                          sx={{
                            paddingTop: (theme) => theme.spacing(1),
                            paddingBottom: (theme) => theme.spacing(1),
                            width: '7.5%',
                          }}
                        >
                          {`R$ ${formatter.format(subRow.value)}`}
                        </RowTableRow>
                        <RowTableRow
                          align="left"
                          sx={{
                            color: 'red',
                            fontWeight: 'medium',
                            paddingTop: (theme) => theme.spacing(1),
                            paddingBottom: (theme) => theme.spacing(1),
                            width: '63.5%',
                          }}
                        >
                          {t('DEBIT')}
                        </RowTableRow>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SharingDetail() {
  const { t } = useTranslation(['sharingDetail', 'common']);
  const { selectedPeriod } = usePeriod();
  const [selectedBuilding, setSelectedBuilding] = useState(1);
  const [selectedSearchCode, setSelectedSearchCode] = useState('');
  const { data: buildings } = useGetActiveBuildingsQuery();
  const { data: sharingResults } = useGetSharingResultByPeriodAndBuildingQuery({
    period: selectedPeriod?.date as string,
    buildingId: selectedBuilding.toString(),
    code: selectedSearchCode,
  });

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Data>('value');
  const handleRequestSortGoals = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const visibleRows = useMemo(() => {
    if (!sharingResults) return [];
    const stabilized = [...sharingResults];
    return stabilized.sort((a, b) => getComparator(order, 'value')(a, b));
  }, [sharingResults, order]);

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginRight: '20px',
      }}
    >
      <PeriodDetails />
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}>
        <Typography variant="h1">{t('sharing.detail')}</Typography>
      </div>
      <Typography>{t('sharing.detail.subtitle')}</Typography>

      <div style={{ width: '100%', marginTop: '24px', marginBottom: '16px', alignItems: 'center', display: 'flex' }}>
        <div style={{ width: '25%' }}>
          <Typography variant="h2">{t('common:building')}</Typography>
          <Select
            size="small"
            sx={{ width: '100%' }}
            value={selectedBuilding}
            onChange={(e) => setSelectedBuilding(e.target.value as number)}
            renderValue={(selected) => buildings?.find((item) => item.id === selected)?.name}
          >
            {buildings?.map((building) => (
              <MenuItem key={building.name} value={building.id}>
                {building.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div style={{ width: '25%', marginLeft: '2rem' }}>
          <Typography variant="h2">{t('expense')}</Typography>
          <TextField
            size="small"
            value={selectedSearchCode}
            onChange={(e) => setSelectedSearchCode(e.target.value)}
            sx={{ width: '100%' }}
            id="outlined-basic"
            variant="outlined"
            placeholder={t('enter.expense.code')}
          />
        </div>
      </div>
      <SharingResultsTable rows={sharingResults || []} />
    </div>
  );
}
export default SharingDetail;
